import { IEvent, default_collection_event, default_pollination_event } from '../../Types/Event';
import { IPrediction_v0 } from '../../Types/Predictions';

import { collectionColor, applicationColor } from '../../utils/colorPercent';
import { addDays, DateRanges } from '../../utils/dateFunctions';

//import { selectUser } from '../actions/actions';

/* {
id	              // String. A unique identifier of an event. Useful for getEventById.
groupId	          // String. Events that share a groupId will be dragged and resized together automatically.
allDay	          // Boolean (true or false). Determines if the event is shown in the “all-day” section of relevant views. In addition, if true the time text is not displayed with the event.
start	            // Date object that obeys the current timeZone. When an event begins.
end	              // Date object that obeys the current timeZone. When an event ends. It could be null if an end wasn’t specified.
                  // Note: This value is exclusive. For example, an event with the end of 2018-09-03 will appear to span through 2018-09-02 but end before the start of 2018-09-03. See how events are are parsed from a plain object for further details.
startStr	        // An ISO8601 string representation of the start date. If the event is all-day, there will not be a time part.
endStr	          // An ISO8601 string representation of the end date. If the event is all-day, there will not be a time part.
title	            // String. The text that will appear on an event.
url	              // String. A URL that will be visited when this event is clicked by the user. For more information on controlling this behavior, see the eventClick callback.
classNames        // An array of strings like [ 'myclass1', myclass2' ]. Determines which HTML classNames will be attached to the rendered event.
editable	        // Boolean (true or false) or null. The value overriding the editable setting for this specific event.
startEditable	    // Boolean (true or false) or null. The value overriding the eventStartEditable setting for this specific event.
durationEditable	// Boolean (true or false) or null. The value overriding the eventDurationEditable setting for this specific event.
resourceEditable	// Boolean (true or false) or null. The value overriding the eventResourceEditable setting for this specific event.
display	          // The rendering type of this event. Can be 'auto', 'block', 'list-item', 'background', 'inverse-background', or 'none'. See eventDisplay.
overlap	          // The value overriding the eventOverlap setting for this specific event. If false, prevents this event from being dragged/resized over other events. Also prevents other events from being dragged/resized over this event. Does not accept a function.
constraint        // The eventConstraint override for this specific event.
backgroundColor	  // The eventBackgroundColor override for this specific event.
borderColor	      // The eventBorderColor override for this specific event.
textColor	        // The eventTextColor override for this specific event.
extendedProps	    // A plain object holding miscellaneous other properties specified during parsing. Receives properties in the explicitly given extendedProps hash as well as other non-standard properties.
source            // A reference to the Event Source this event came from. If the event was added dynamically via addEvent, and the source parameter was not specified, this value will be null.
} */

// export const INITIAL_RESOURCES = [
//   { "id": "1", "title": "Field 1" },
//   { "id": "2", "title": "Field 2", "eventColor": "green" },
//   { "id": "3", "title": "Field 3", "eventColor": "orange" },
//   { "id": "4", "title": "Field 4", "children": [{ "id": "d1", "title": "Room D1" },
//                                           { "id": "5", "title": "Room D2" }] },
//   { "id": "e", "title": "Auditorium E" },
//   { "id": "f", "title": "Auditorium F", "eventColor": "red" }



// const default_weather_event: IEvent = {
//   allDay: true,
//   editable: false,
//   startEditable: false,
//   durationEditable: false,
//   resourceEditable: false,
//   display: 'background',
//   //   backgroundColor: 'transparent', -- can be a css gradiant
//   field: false, weather: true
// }


const translatePollinationEvents = (data: IPrediction_v0): (IEvent[]) => {
  let r: IEvent[] = [];
  if (data == null) {
    return r;
  }

  // check in case there are no good days
  if (!data.hasOwnProperty('bestdays') ||
    data.bestdays == null ||
    data.bestdays.length === 0) {
    // console.log('has no bestdays');
    return r;
  }

  // // Get the range of values for this set and spread the colors accordingly. This way all sets look visually the same
  // // in color/shading for worst/best.
  // const increaseMin = 100 * data.bestdays.reduce((min, i) => i.percent_increase < min ? i.percent_increase : min, data.bestdays[0].percent_increase);
  // const increaseMax = 100 * data.bestdays.reduce((max, i) => i.percent_increase > max ? i.percent_increase : max, data.bestdays[0].percent_increase);
  // const range = increaseMax - increaseMin;
  const bestDates = data.bestdays.map(b => b.day);
  const ranges = DateRanges(bestDates);
  const populatedRanges = ranges.filter(r => r.length > 0);

  r = populatedRanges.map(dateRange => {
    const first_day = dateRange[0];
    var last_day = first_day;
    if (dateRange.length > 1) {
      last_day = dateRange[dateRange.length - 1];
    }

    const exclusive_last_day: Date = new Date(addDays(last_day, 1));

    return {
      ...default_pollination_event,
      prediction: data,
      id: data.id,
      
      // resources currently can't be passed as a string array. So make a comma delimited string to represent multiple resourceids on the same event.
      // resourceId: [
      //   data.id + default_pollination_event.eventType,
      //   data.id
      // ],
      resourceId: data.id + default_pollination_event.eventType,
      resourceName: data.name,
      display: 'auto',

      title: data.name,
      start: first_day.toISOString(),
      end: exclusive_last_day.toISOString(),
      //background: "linear-gradient(90deg, pink 80%, cyan 0%)"
      background: applicationColor,
      borderColor: applicationColor,
    } as IEvent;
  });

  return r;
};

const translateCollectionEvents = (data: IPrediction_v0): (IEvent[]) => {
  let r: IEvent[] = [];
  if (data == null) {
    return r;
  }

  // check in case there are no good days
  if (!data.hasOwnProperty('bestcollection') ||
    data.bestcollection == null ||
    data.bestcollection.length === 0) {
    return r;
  }

  const bestDates = data.bestcollection.map(b => b.day);
  const ranges = DateRanges(bestDates);
  const populatedRanges = ranges.filter(r => r.length > 0);

  r = populatedRanges.map(dateRange => {
    const first_day = dateRange[0];
    var last_day = first_day;
    if (dateRange.length > 1) {
      last_day = dateRange[dateRange.length - 1];
    }
    const exclusive_last_day: Date = new Date(addDays(last_day, 1));

    return {
      ...default_collection_event,
      prediction: data,
      id: data.id,
      resourceId: data.id + default_collection_event.eventType,
      resourceName: data.name,
      display: 'auto',

      title: data.name,
      start: first_day.toISOString(),
      end: exclusive_last_day.toISOString(),
      //background: "linear-gradient(90deg, pink 80%, cyan 0%)"
      borderColor: collectionColor,
      background: collectionColor,
    } as IEvent;
  });

  return r;
};


export const translateEvents = (data: IPrediction_v0[]): (IEvent[]) => {
  const p: IEvent[] = data.map(translatePollinationEvents).flat();
  const c: IEvent[] = data.map(translateCollectionEvents).flat();

  return c.concat(p);
}